import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { Dictionary } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Destroyer } from 'src/app/core/abstract/destroyer';
import { StringAnyMap } from 'src/app/core/models/common.models';
@Component({
  template: '',
  standalone: false,
})
export abstract class BaseSingleSelectComponent extends Destroyer implements OnInit, OnChanges, ControlValueAccessor {
  @Input() public items: ReadonlyArray<any> | Readonly<StringAnyMap>;
  @Input() public idFieldName: string;
  @Input() public labelFieldName: string;
  @Input() public searchVisibleCount = 10;
  @Input() public unselectMode = false;
  @Input() public disabled = false;
  @Input() public disableRipple = false;
  @Input() public allowNullValue = false;
  @Input() public nullValueLabel?: string = 'None';
  @Input() public nullValueId?: string | number | null = null;
  @Input() public searchPlaceholder = 'Search';
  @Input() public selectedId: string | number | undefined | null;
  @Input() public hiddenIds?: Dictionary<boolean>;

  @Output() public select = new EventEmitter<any>();
  @Output() public opened = new EventEmitter<void>();
  @Output() public closed = new EventEmitter<void>();
  @Output() public unselect = new EventEmitter<any>();

  @ViewChild('searchInput') public searchInput: ElementRef;

  public itemsCount = 0;
  public selectedItem: any;
  public filteredItems: Observable<any[]>;
  public searchControl = new UntypedFormControl();

  private itemsChanged$ = new BehaviorSubject<void>(void 0);

  protected onTouched: () => void = () => {};
  protected onChange: (_: any) => void = (_) => {};

  constructor(protected cdRef: ChangeDetectorRef) {
    super();
    this.filteredItems = this.itemsChanged$.pipe(
      takeUntil(this.destroy$),
      map(() => this.filterItems(this.searchControl.value)),
    );
  }

  public get hasSearch(): boolean {
    return this.itemsCount >= this.searchVisibleCount;
  }

  public get selectedName(): string | undefined {
    return this.selectedId === this.nullValueId && this.allowNullValue
      ? this.nullValueLabel
      : this.selectedItem?.[this.labelFieldName];
  }

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        distinctUntilChanged(), //
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.itemsChanged$.next();
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.items) {
      this.setItems(changes.items.currentValue, this.selectedId);
    }

    if (changes.hiddenIds) {
      this.itemsChanged$.next();
    }

    if (changes.selectedId && !changes.selectedId.isFirstChange()) {
      this.setSelectedItem();
    }
  }

  public identify = (index: number, item: any): string | number => {
    return item?.[this.idFieldName] || index;
  };

  public onMenuOpened() {
    this.onTouched();
    this.opened.next();
  }

  public onMenuClosed() {
    this.searchControl.setValue('');
    this.closed.next();
  }

  public selectItem(item: any) {
    this.changeSelectedItem(item);
    this.onChange(this.selectedId);
  }

  public setNullValue() {
    this.selectedItem = void 0;
    this.selectedId = this.nullValueId;

    this.onChange(this.selectedId);
    this.select.next({ [this.idFieldName]: this.selectedId, [this.labelFieldName]: this.nullValueLabel });
  }

  public writeValue(value: any): void {
    this.selectedId = value;
    this.setSelectedItem();
  }

  public registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdRef.detectChanges();
  }

  protected setItems(items: any[] | StringAnyMap, selectedId: string | number | undefined | null) {
    this.items = items;
    this.selectedId = selectedId;

    this.setItemsCount();
    this.setSelectedItem();
    this.itemsChanged$.next();
  }

  protected filterItems(searchText: string) {
    let result = this.items ? (Array.isArray(this.items) ? this.items : Object.values(this.items)) : [];

    if (searchText) {
      const text = searchText.toLowerCase();
      result = result.filter((item) => {
        const label = String(item[this.labelFieldName] || '');
        return label.toLowerCase().includes(text);
      });
    }

    if (this.hiddenIds) {
      result = result.filter((item) => {
        const id = item[this.idFieldName];
        return !this.hiddenIds![id];
      });
    }

    return result;
  }

  protected setSelectedItem() {
    const items = this.items;
    if (Array.isArray(items)) {
      this.selectedItem = items.find((item) => item[this.idFieldName] === this.selectedId);
    } else if (this.selectedId) {
      this.selectedItem = (items as any)?.[this.selectedId];
    } else {
      this.selectedItem = void 0;
    }
  }

  protected setItemsCount() {
    const items = this.items;
    this.itemsCount = items ? (Array.isArray(items) ? items.length : Object.keys(items).length) : 0;
  }

  protected changeSelectedItem(item: any) {
    if (!this.unselectMode || this.selectedId !== item[this.idFieldName]) {
      this.selectedId = item[this.idFieldName];
      this.selectedItem = item;
      this.select.next(item);
    } else if (this.selectedId === item[this.idFieldName]) {
      this.selectedId = void 0;
      this.selectedItem = void 0;
      this.unselect.next(item);
    }
  }
}

@Component({
  selector: 'craft-single-select',
  templateUrl: './craft-single-select.component.html',
  styleUrls: ['./craft-single-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CraftSingleSelectComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CraftSingleSelectComponent extends BaseSingleSelectComponent {
  @Input() public items: ReadonlyArray<any> | Readonly<StringAnyMap>;
  @Input() public idFieldName: string;
  @Input() public labelFieldName: string;
  @Input() public selectedId: string | number | undefined | null;
  @Input() public searchVisibleCount = 10;
  @Input() public unselectMode = false;
  @Input() public disabled = false;
  @Input() public disableRipple = false;
  @Input() public searchPlaceholder = 'Search';

  @Output() public select = new EventEmitter<any>();
  @Output() public opened = new EventEmitter<void>();
  @Output() public closed = new EventEmitter<void>();
  @Output() public unselect = new EventEmitter<any>();

  constructor(protected cdRef: ChangeDetectorRef) {
    super(cdRef);
  }
}
